import { supabase } from '../lib/supabase';
import { wikipediaService } from './wikipediaService';
import { applyCountryFilter, getStoredLocalePrefs } from '../lib/countryQuery';
import { INTERNATIONAL_CODE } from '../data/countries';

const LOGO_DEV_TOKEN = import.meta.env.VITE_LOGO_DEV_TOKEN || '';

/**
 * Get a real logo URL for a company.
 * Priority: logo_url from DB → logo.dev via website domain → colored initial (null).
 */
function getCompanyLogo(company) {
  // 1. Already has a logo URL stored in DB.
  //    Clearbit's free logo API was sunset, so rewrite those dead URLs to
  //    logo.dev (same domain) which we already have a token for.
  if (company?.logo_url) {
    const clearbit = company.logo_url.match(/logo\.clearbit\.com\/([^/?#]+)/i);
    if (clearbit && LOGO_DEV_TOKEN) {
      return `https://img.logo.dev/${clearbit[1]}?token=${LOGO_DEV_TOKEN}&size=128&format=png`;
    }
    return company.logo_url;
  }

  // 2. Derive from website domain via logo.dev
  if (company?.website && LOGO_DEV_TOKEN) {
    try {
      const domain = new URL(company.website).hostname.replace('www.', '');
      return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128&format=png`;
    } catch { /* bad URL, fall through */ }
  }

  // 3. Try name-based domain guess via logo.dev
  if (company?.name && LOGO_DEV_TOKEN) {
    const guess = company.name.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com';
    return `https://img.logo.dev/${guess}?token=${LOGO_DEV_TOKEN}&size=128&format=png`;
  }

  // 4. No logo available — return null so the UI can show an initial badge
  return null;
}

export const companyService = {
  // Enhanced getFeaturedCompanies with proper schema alignment
  async getFeaturedCompanies(limit = 9, offset = 0) {
    try {
      // FIXED: Removed .eq('is_active', true) - column doesn't exist in schema
      // Exclude brand/product rows (they carry a parent_company) so featured stays clean
      const { data, error } = await supabase?.from('companies')?.select('*')?.is('parent_company', null)?.order('created_at', { ascending: false })
        ?.range(offset, offset + limit - 1);

      if (error) throw error;

      // Transform database companies to UI format with realistic profit calculation
      return data?.map((company) => {
        // FIXED: Use actual schema columns - revenue field exists in schema
        // Calculate 4-year profit estimate based on revenue (industry standard: 10-15% profit margin)
        const profitMargin = 0.12; // 12% average profit margin
        const yearlyProfit = (company?.revenue || 0) * profitMargin;
        const fourYearProfits = yearlyProfit * 4;
        
        // Focus on profit-based valuation 
        const currentValue = {
          value: fourYearProfits,
          totalNetProfits: fourYearProfits,
          isProfitBased: true,
          isPositive: fourYearProfits > 0,
          changeFormatted: fourYearProfits > 0 ? '+' + this.formatCurrency(yearlyProfit) : this.formatCurrency(yearlyProfit)
        };

        return {
          id: company?.id,
          name: company?.name,
          industry: company?.industry || 'Technology',
          logo: getCompanyLogo(company),
          logoAlt: `${company?.name} company logo`,
          currentValue: currentValue,
          countryOfOrigin: company?.country_of_origin || 'United States',
          parentCompany: company?.parent_company,
          foundedYear: company?.founded_year,
          employees: company?.employees,

          // Political contributions — future: aggregate from executive PAC data
          politicalContributions: {
            conservativePacs: 0,
            liberalPacs: 0,
            thinkTanks: 0,
            totalAmount: 0
          },

          // Key stakeholders - using defaults since columns don't exist
          keyInvestors: ['Institutional Investors', 'Private Equity', 'Public Markets'],
          celebrities: ['Various Influencers', 'Brand Ambassadors'],

          // Timestamps
          createdAt: company?.created_at,
          updatedAt: company?.updated_at
        };
      });
    } catch (error) {
      console.error('Error fetching featured companies:', error);
      throw error;
    }
  },

  // NEW: Sync company data with Wikipedia
  async syncCompanyWithWikipedia(companyName) {
    try {
      const wikipediaData = await wikipediaService?.getCompanyInfo(companyName);
      if (!wikipediaData) return null;

      // Check if company exists in database
      const { data: existingCompany, error: fetchError } = await supabase
        ?.from('companies')
        ?.select('*')
        ?.eq('name', companyName)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        throw fetchError;
      }

      const companyData = {
        name: wikipediaData?.name,
        industry: wikipediaData?.industry,
        description: wikipediaData?.description,
        logo_url: wikipediaData?.logoUrl,
        website: wikipediaData?.website,
        headquarters: wikipediaData?.headquarters,
        founded_year: wikipediaData?.foundedYear,
        employees: wikipediaData?.employees,
        revenue: wikipediaData?.revenue,
        country_of_origin: wikipediaData?.countryOfOrigin,
        parent_company: wikipediaData?.parentCompany,
        updated_at: new Date()?.toISOString()
      };

      if (existingCompany) {
        // Update existing company
        const { data, error } = await supabase
          ?.from('companies')
          ?.update(companyData)
          ?.eq('id', existingCompany?.id)
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      } else {
        // Insert new company
        const { data, error } = await supabase
          ?.from('companies')
          ?.insert(companyData)
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error syncing company with Wikipedia:', error);
      throw error;
    }
  },

  // NEW: Bulk sync multiple companies with Wikipedia
  async bulkSyncWithWikipedia(companyNames) {
    const results = [];
    
    for (const companyName of companyNames) {
      try {
        const result = await this.syncCompanyWithWikipedia(companyName);
        results?.push({ companyName, success: !!result, data: result });
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Failed to sync ${companyName}:`, error);
        results?.push({ companyName, success: false, error: error?.message });
      }
    }

    return results;
  },

  // Get specific company by ID
  async getCompanyById(id) {
    try {
      const { data, error } = await supabase?.from('companies')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      // Transform to camelCase
      return {
        id: data?.id,
        name: data?.name,
        industry: data?.industry,
        logo: data?.logo_url,
        logoAlt: `${data?.name} company logo and branding`,
        website: data?.website,
        description: data?.description,
        headquarters: data?.headquarters,
        foundedYear: data?.founded_year,
        employees: data?.employees,
        revenue: data?.revenue,
        marketCap: data?.market_cap,
        stockSymbol: data?.stock_symbol,
        companyType: data?.company_type,
        isVerified: data?.is_verified,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        createdBy: data?.created_by,
        
        // Real-time value ticker with revenue
        currentValue: this.calculateCurrentValue(data?.revenue)
      };
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  },

  // Helper to calculate current value from revenue
  calculateCurrentValue(revenue) {
    const profitMargin = 0.12;
    const yearlyProfit = (revenue || 0) * profitMargin;
    const fourYearProfits = yearlyProfit * 4;
    
    return {
      value: fourYearProfits,
      totalNetProfits: fourYearProfits,
      isProfitBased: true,
      isPositive: fourYearProfits > 0,
      changeFormatted: fourYearProfits > 0 ? '+' + this.formatCurrency(yearlyProfit) : this.formatCurrency(yearlyProfit)
    };
  },

  formatCurrency(amount) {
    if (!amount) return '$0';
    if (amount >= 1000000000) {
      return `$${(amount / 1000000000)?.toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `$${(amount / 1000000)?.toFixed(1)}M`;
    }
    return `$${amount?.toLocaleString()}`;
  },

  // NEW: Get exactly 3 random companies/brands for homepage
  async getRandomFeaturedCompanies(count = 3, options = {}) {
    try {
      const prefs = options.countryCode ? options : getStoredLocalePrefs();
      const countryCode = options.countryCode || prefs.countryCode;
      const includeInternational = options.includeInternational ?? prefs.includeInternational ?? true;

      let countQuery = supabase?.from('companies')?.select('*', { count: 'exact', head: true })?.is('parent_company', null);
      if (countryCode && countryCode !== INTERNATIONAL_CODE) {
        countQuery = applyCountryFilter(countQuery, countryCode, { table: 'companies', includeInternational });
      }
      const { count: totalCount, error: countError } = await countQuery;

      if (countError) throw countError;

      // Get random offset
      const maxOffset = Math.max(0, (totalCount || 0) - count);
      const randomOffset = Math.floor(Math.random() * (maxOffset + 1));

      let query = supabase
        ?.from('companies')
        ?.select(`
          *,
          executives!company_id(
            id,
            user_id,
            position,
            is_current,
            user_profiles!user_id(
              full_name,
              political_leaning
            )
          ),
          investments!company_id(
            id,
            investor_id,
            investment_amount,
            is_active,
            user_profiles!investor_id(
              full_name
            )
          )
        `)
        ?.is('parent_company', null)
        ?.order('revenue', { ascending: false });

      if (countryCode && countryCode !== INTERNATIONAL_CODE) {
        query = applyCountryFilter(query, countryCode, { table: 'companies', includeInternational });
      }

      const { data, error } = await query?.range(randomOffset, randomOffset + count - 1);

      if (error) throw error;

      // Transform database companies to UI format with all detailed information
      return data?.map((company) => {
        // Calculate 4-year profit estimate
        const profitMargin = 0.12; // 12% average profit margin
        const yearlyProfit = (company?.revenue || 0) * profitMargin;
        const fourYearProfits = yearlyProfit * 4;
        
        // Generate 4-year profit breakdown (deterministic growth curve based on revenue)
        const currentYear = new Date()?.getFullYear();
        const growthFactors = [0.88, 0.94, 1.0, 1.06]; // steady upward trend
        const profitYears = growthFactors.map((factor, i) => {
          const profit = Math.floor(yearlyProfit * factor);
          return {
            year: currentYear - (3 - i),
            profit,
            formatted: this.formatCurrency(profit)
          };
        });

        // UPDATED: Count actual executives and investors from database
        const executivesCount = company?.executives?.filter(exec => exec?.is_current)?.length || 0;
        const investorsCount = company?.investments?.filter(inv => inv?.is_active)?.length || 0;
        
        // UPDATED: Extract top investor names (max 3)
        const topInvestorNames = company?.investments
          ?.filter(inv => inv?.is_active && inv?.user_profiles?.full_name)
          ?.slice(0, 3)
          ?.map(inv => inv?.user_profiles?.full_name) || [];

        // UPDATED: Count subsidiaries (companies with this company as parent)
        const hasSubsidiaries = company?.parent_company === null; // Top-level companies likely have subsidiaries

        return {
          id: company?.id,
          type: 'company', // Flag to identify card type
          name: company?.name,
          industry: company?.industry || 'Technology',
          logo: getCompanyLogo(company),
          logoAlt: `${company?.name} company logo`,
          countryOfOrigin: company?.country_of_origin || 'United States',
          parentCompany: company?.parent_company,
          foundedYear: company?.founded_year,
          employees: company?.employees,
          headquarters: company?.headquarters,

          // 4-year profit data (NO stock price or valuation)
          currentValue: {
            value: fourYearProfits,
            baseRevenue: company?.revenue,
            totalNetProfits: fourYearProfits,
            yearlyProfit: yearlyProfit,
            profitYears: profitYears,
            change: 6.0, // based on growthFactors trend (6% YoY)
            changeFormatted: '+6.0%',
            isPositive: true,
            isProfitBased: true,
            isRevenueBased: false
          },

          // UPDATED: Relationship metrics - actual counts from database
          relationshipMetrics: {
            executivesCount: executivesCount,
            investorsCount: investorsCount,
            subsidiariesCount: 0, // Will populate when subsidiaries table is added
            parentCompany: company?.parent_company
          },

          // Political contributions — future: aggregate from executive PAC data
          politicalContributions: {
            conservativePacs: 0,
            liberalPacs: 0,
            thinkTanks: 0,
            totalAmount: '$0'
          },

          // UPDATED: Top investor names for display
          keyInvestors: topInvestorNames?.length > 0 
            ? topInvestorNames 
            : ['No investors tracked'],

          // Celebrity endorsers — future: separate table
          celebrities: [],

          // Timestamps
          createdAt: company?.created_at,
          updatedAt: company?.updated_at
        };
      });
    } catch (error) {
      console.error('Error fetching random featured companies:', error);
      throw error;
    }
  },

  /**
   * Get FULL company profile with all relationships (for company profile page).
   * Includes executives with user profiles, investors with user profiles,
   * and Wikipedia enrichment.
   */
  async getCompanyFullProfile(id) {
    try {
      const { data, error } = await supabase
        ?.from('companies')
        ?.select(`
          *,
          executives(
            id, position, is_current, start_date, equity_percentage,
            user:user_profiles(
              id, full_name, avatar_url, bio, location, political_leaning,
              political_score, companies_owned_count, companies_invested_count
            )
          ),
          investments(
            id, investment_amount, equity_percentage, investment_type,
            is_active, investment_date,
            investor:user_profiles(
              id, full_name, avatar_url, bio, location, political_leaning,
              political_score
            )
          )
        `)
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      if (!data) throw new Error('Company not found');

      // Try Wikipedia enrichment for the company
      let wikiData = null;
      try {
        wikiData = await wikipediaService?.getCompanyInfo(data.name);
      } catch (e) {
        console.warn('Wikipedia enrichment failed for company:', data.name);
      }

      // Build executives list
      const executives = (data.executives || [])
        .filter(exec => exec.user)
        .map(exec => ({
          id: exec.id,
          userId: exec.user.id,
          name: exec.user.full_name,
          role: exec.position,
          startDate: exec.start_date,
          isCurrent: exec.is_current,
          equityPercentage: parseFloat(exec.equity_percentage || 0),
          photo: exec.user.avatar_url,
          photoAlt: `${exec.user.full_name} profile photo`,
          bio: exec.user.bio,
          location: exec.user.location,
          politicalLeaning: exec.user.political_leaning,
        }));

      // Build investors list
      const investors = (data.investments || [])
        .filter(inv => inv.investor)
        .map(inv => ({
          id: inv.id,
          userId: inv.investor.id,
          name: inv.investor.full_name,
          investmentAmount: inv.investment_amount,
          investmentAmountFormatted: inv.investment_amount
            ? this.formatCurrency(inv.investment_amount)
            : 'Undisclosed',
          equityPercentage: parseFloat(inv.equity_percentage || 0),
          investmentType: inv.investment_type,
          isActive: inv.is_active,
          investmentDate: inv.investment_date,
          photo: inv.investor.avatar_url,
          photoAlt: `${inv.investor.full_name} profile photo`,
          bio: inv.investor.bio,
          location: inv.investor.location,
        }));

      // Build ownership chart data
      const insidersTotal = executives.reduce((sum, e) => sum + (e.equityPercentage || 0), 0);
      const investorsTotal = investors.reduce((sum, i) => sum + (i.equityPercentage || 0), 0);
      const publicFloat = Math.max(0, 100 - insidersTotal - investorsTotal);

      const ownershipData = {
        lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        chartData: [
          { name: 'Executives & Insiders', percentage: insidersTotal, shares: `${insidersTotal.toFixed(1)}%` },
          { name: 'Investors', percentage: investorsTotal, shares: `${investorsTotal.toFixed(1)}%` },
          { name: 'Public / Other', percentage: publicFloat, shares: `${publicFloat.toFixed(1)}%` },
        ].filter(d => d.percentage > 0),
        majorShareholders: [
          ...executives.filter(e => e.equityPercentage > 0).map(e => ({
            id: e.userId,
            name: e.name,
            type: 'individual',
            title: e.role,
            percentage: e.equityPercentage,
            shares: `${e.equityPercentage.toFixed(2)}%`,
            avatar: e.photo,
            avatarAlt: e.photoAlt,
          })),
          ...investors.filter(i => i.equityPercentage > 0).map(i => ({
            id: i.userId,
            name: i.name,
            type: 'individual',
            category: 'Investor',
            percentage: i.equityPercentage,
            shares: `${i.equityPercentage.toFixed(2)}%`,
            avatar: i.photo,
            avatarAlt: i.photoAlt,
          })),
        ].sort((a, b) => b.percentage - a.percentage),
        detailedBreakdown: [
          {
            id: 'insiders',
            title: 'Company Insiders & Executives',
            icon: 'Users',
            count: executives.length,
            entities: executives.map(e => ({
              id: e.userId,
              name: e.name,
              type: 'individual',
              description: e.role,
              percentage: e.equityPercentage,
              avatar: e.photo,
              avatarAlt: e.photoAlt,
            })),
          },
          {
            id: 'investors',
            title: 'Investors',
            icon: 'TrendingUp',
            count: investors.length,
            entities: investors.map(i => ({
              id: i.userId,
              name: i.name,
              type: 'individual',
              description: `${i.investmentAmountFormatted} invested`,
              percentage: i.equityPercentage,
              avatar: i.photo,
              avatarAlt: i.photoAlt,
            })),
          },
        ],
      };

      // Build timeline from Wikipedia or from relationships
      const timelineEvents = [];
      if (data.founded_year) {
        timelineEvents.push({
          id: 1,
          title: `${data.name} Founded`,
          date: `${data.founded_year}`,
          category: 'founding',
          description: `${data.name} was founded${data.headquarters ? ` in ${data.headquarters}` : ''}.`,
          financialImpact: null,
        });
      }
      executives.forEach((exec, idx) => {
        if (exec.startDate) {
          timelineEvents.push({
            id: 100 + idx,
            title: `${exec.name} appointed ${exec.role}`,
            date: exec.startDate,
            category: 'leadership',
            description: `${exec.name} became ${exec.role} at ${data.name}.`,
            keyFigures: [{ name: exec.name, avatar: exec.photo, avatarAlt: exec.photoAlt }],
          });
        }
      });
      timelineEvents.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });

      // Fetch related companies (same industry)
      let relatedCompanies = { subsidiaries: [], parent: [], partners: [], competitors: [] };
      try {
        const { data: sameIndustry } = await supabase
          ?.from('companies')
          ?.select('id, name, industry, logo_url, headquarters, founded_year, employees, revenue, website, description, country_of_origin')
          ?.eq('industry', data.industry)
          ?.neq('id', id)
          ?.limit(6);

        if (sameIndustry && sameIndustry.length > 0) {
          relatedCompanies.competitors = sameIndustry.map(c => ({
            id: c.id,
            name: c.name,
            industry: c.industry,
            logo: c.logo_url,
            logoAlt: `${c.name} logo`,
            description: c.description || `${c.name} operates in the ${c.industry} industry.`,
            relationship: 'competitor',
            employees: c.employees ? c.employees.toLocaleString() : null,
            revenue: c.revenue ? this.formatCurrency(c.revenue) : null,
            founded: c.founded_year,
            location: c.headquarters || c.country_of_origin,
          }));
        }
      } catch (e) {
        console.warn('Failed to fetch related companies:', e);
      }

      // Revenue & profit calculations
      const profitMargin = 0.12;
      const yearlyProfit = (data.revenue || 0) * profitMargin;
      const marketCapStr = data.market_cap
        ? this.formatCurrency(data.market_cap)
        : (data.revenue ? this.formatCurrency(data.revenue * 5) : 'N/A');

      return {
        // Core company data
        id: data.id,
        name: data.name,
        ticker: data.stock_symbol || null,
        industry: data.industry || 'Business',
        logo: data.logo_url,
        logoAlt: `${data.name} company logo`,
        description: wikiData?.description || data.description ||
          `${data.name} is a company operating in the ${data.industry || 'business'} sector.`,
        marketCap: marketCapStr,
        employees: data.employees ? data.employees.toLocaleString() : 'N/A',
        founded: data.founded_year || 'N/A',
        headquarters: data.headquarters || data.country_of_origin || 'N/A',
        website: data.website,
        revenue: data.revenue,
        revenueFormatted: data.revenue ? this.formatCurrency(data.revenue) : 'N/A',
        yearlyProfit: yearlyProfit,
        yearlyProfitFormatted: yearlyProfit ? this.formatCurrency(yearlyProfit) : 'N/A',
        countryOfOrigin: data.country_of_origin,
        companyType: data.company_type,

        // People
        executives,
        investors,

        // Ownership
        ownershipData,

        // Timeline
        timelineData: { events: timelineEvents },

        // Related companies
        relatedCompanies,

        // Timestamps
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('Error fetching full company profile:', error);
      throw error;
    }
  },

  /**
   * Logo Wall — lightweight list of every company/brand that has a
   * resolvable logo. Returns simple cards for the grid.
   */
  async getLogoWallCompanies() {
    try {
      const { data, error } = await supabase
        ?.from('companies')
        ?.select('id, name, industry, logo_url, website, company_type, parent_company')
        ?.order('name', { ascending: true });

      if (error) throw error;

      return (data || [])
        .map((c) => ({
          id: c.id,
          name: c.name,
          industry: c.industry || 'Business',
          logo: getCompanyLogo(c),
          logoAlt: `${c.name} logo`,
          companyType: c.company_type,
          parentCompany: c.parent_company,
          website: c.website,
        }))
        .filter((c) => !!c.logo);
    } catch (error) {
      console.error('Error fetching logo wall companies:', error);
      throw error;
    }
  },

  /**
   * Logo Wall modal data — founders / executives / investors / organizations
   * for a company. If the tapped company (usually a product/brand) has no
   * people of its own, resolve to its parent company and use theirs.
   */
  async getCompanyConnections(companyId) {
    const fetchWithPeople = async (id) => {
      const { data, error } = await supabase
        ?.from('companies')
        ?.select(`
          id, name, industry, logo_url, website, company_type, parent_company, description,
          executives(
            id, position, is_current, equity_percentage,
            user:user_profiles(id, full_name, avatar_url, bio, location, political_leaning)
          ),
          investments(
            id, investment_amount, equity_percentage, investment_type, is_active,
            investor:user_profiles(id, full_name, avatar_url, bio, location, political_leaning)
          )
        `)
        ?.eq('id', id)
        ?.single();
      if (error) throw error;
      return data;
    };

    const hasPeople = (c) =>
      (c?.executives?.length || 0) > 0 || (c?.investments?.length || 0) > 0;

    try {
      let company = await fetchWithPeople(companyId);
      let resolvedFrom = null;

      // Resolve to parent company when the brand/product has no people
      if (!hasPeople(company) && company?.parent_company) {
        const { data: parentRows } = await supabase
          ?.from('companies')
          ?.select('id')
          ?.ilike('name', company.parent_company)
          ?.limit(1);

        if (parentRows && parentRows.length > 0) {
          const parentFull = await fetchWithPeople(parentRows[0].id);
          if (hasPeople(parentFull)) {
            resolvedFrom = { id: company.id, name: company.name };
            company = parentFull;
          }
        }
      }

      const allExecs = (company.executives || [])
        .filter((e) => e.user)
        .map((e) => ({
          id: e.id,
          userId: e.user.id,
          name: e.user.full_name,
          role: e.position || 'Executive',
          isCurrent: e.is_current,
          equityPercentage: parseFloat(e.equity_percentage || 0),
          photo: e.user.avatar_url,
          photoAlt: `${e.user.full_name} profile photo`,
          bio: e.user.bio,
          location: e.user.location,
          politicalLeaning: e.user.political_leaning,
        }));

      const isFounder = (role) => /founder/i.test(role || '');
      const founders = allExecs.filter((e) => isFounder(e.role));
      const executives = allExecs.filter((e) => !isFounder(e.role));

      const investors = (company.investments || [])
        .filter((i) => i.investor)
        .map((i) => ({
          id: i.id,
          userId: i.investor.id,
          name: i.investor.full_name,
          investmentAmount: i.investment_amount,
          investmentAmountFormatted: i.investment_amount
            ? this.formatCurrency(i.investment_amount)
            : 'Undisclosed',
          equityPercentage: parseFloat(i.equity_percentage || 0),
          investmentType: i.investment_type,
          isActive: i.is_active,
          photo: i.investor.avatar_url,
          photoAlt: `${i.investor.full_name} profile photo`,
          bio: i.investor.bio,
          location: i.investor.location,
          politicalLeaning: i.investor.political_leaning,
        }));

      // Aggregate organizations from all involved people
      const userIds = Array.from(
        new Set([
          ...allExecs.map((e) => e.userId),
          ...investors.map((i) => i.userId),
        ].filter(Boolean))
      );

      let organizations = [];
      if (userIds.length > 0) {
        const { data: orgRows } = await supabase
          ?.from('organizational_associations')
          ?.select('organization_name, organization_type, relationship_type, user_id')
          ?.in('user_id', userIds)
          ?.eq('is_active', true);

        const seen = new Set();
        organizations = (orgRows || [])
          .filter((o) => {
            const key = (o.organization_name || '').toLowerCase();
            if (!o.organization_name || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((o) => ({
            name: o.organization_name,
            type: o.organization_type,
            relationship: o.relationship_type,
          }));
      }

      return {
        company: {
          id: company.id,
          name: company.name,
          industry: company.industry || 'Business',
          logo: getCompanyLogo(company),
          logoAlt: `${company.name} logo`,
          website: company.website,
          description: company.description,
          parentCompany: company.parent_company,
          companyType: company.company_type,
        },
        resolvedFrom,
        founders,
        executives,
        investors,
        organizations,
      };
    } catch (error) {
      console.error('Error fetching company connections:', error);
      throw error;
    }
  },

  // Create new company
  async createCompany(companyData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbData = {
        name: companyData?.name,
        industry: companyData?.industry,
        logo_url: companyData?.logoUrl,
        website: companyData?.website,
        description: companyData?.description,
        headquarters: companyData?.headquarters,
        founded_year: companyData?.foundedYear,
        employees: companyData?.employees,
        revenue: companyData?.revenue,
        market_cap: companyData?.marketCap,
        stock_symbol: companyData?.stockSymbol,
        company_type: companyData?.companyType || 'private',
        country_of_origin: companyData?.countryOfOrigin,
        parent_company: companyData?.parentCompany,
        created_by: session?.user?.id
      };

      const { data, error } = await supabase?.from('companies')?.insert(dbData)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        name: data?.name,
        industry: data?.industry,
        logo: data?.logo_url,
        logoAlt: `${data?.name} company logo and branding`,
        website: data?.website,
        description: data?.description,
        headquarters: data?.headquarters,
        foundedYear: data?.founded_year,
        employees: data?.employees,
        revenue: data?.revenue,
        marketCap: data?.market_cap,
        stockSymbol: data?.stock_symbol,
        companyType: data?.company_type,
        isVerified: data?.is_verified,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        createdBy: data?.created_by
      };
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }
};